<template>
  <div
    ref="editorContainer"
    class="overflow-hidden flex flex-auto self-start ma-auto h-full w-full"
    @click="closeAnnotationMenu"
  ></div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, toRefs } from 'vue'
import { useAppStore } from '@collection-player/stores/app.store'
import type { MarkerArea as MarkerAreaType } from '@markerjs/markerjs3'
import { useMarkerjs3 } from '@collection-player/composables/markerjs.use.ts'

type ScalingMode = 'fit-content' | 'fit-width' | 'fit-height'

const props = defineProps<{
  targetImage: string
  aspectRatio: number | null
  imageWidth: number
  imageHeight: number
  containerWidth: number
  containerHeight: number
  isActive: boolean
  zoom: number
  scalingMode: 'fit-content' | 'fit-width' | 'fit-height'
}>()

const emit = defineEmits<{
  repaint: []
}>()

const { targetImage, imageWidth, imageHeight, containerWidth, containerHeight, zoom, scalingMode, isActive } =
  toRefs(props)
const { annotationLayer, zoom: zoomInstance } = useAppStore()
const editorContainer = ref<HTMLDivElement | null>(null)
const editor = ref<MarkerAreaType | null>(null)
const imageElement = document.createElement('img')

const closeAnnotationMenu = () => {
  if (annotationLayer.isVisibleMenu.value) {
    annotationLayer.showMenu(false)
  }
}

function updateMarkerImage(markerArea: MarkerAreaType, newImageUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!markerArea.targetImage) {
      reject('No target image provided')
      return
    }

    markerArea.targetImage.onload = () => {
      resolve()
    }

    markerArea.targetImage.src = newImageUrl

    markerArea.targetImage.onerror = () => {
      reject(new Error(`Failed to load image: ${newImageUrl}`))
    }
  })
}

async function scaleMarkerArea(
  markerInstance: MarkerAreaType,
  containerWidth: number,
  containerHeight: number,
  mode: ScalingMode,
) {
  const PADDING = 10

  if (!markerInstance?.targetImage) return

  // @ts-expect-error access private property
  const scrollContainer = markerInstance._canvasContainer as HTMLElement

  let scrollXPercent = 0
  let scrollYPercent = 0

  if (scrollContainer) {
    const maxScrollX = scrollContainer.scrollWidth - scrollContainer.clientWidth
    const maxScrollY = scrollContainer.scrollHeight - scrollContainer.clientHeight

    scrollXPercent = maxScrollX > 0 ? scrollContainer.scrollLeft / maxScrollX : 0
    scrollYPercent = maxScrollY > 0 ? scrollContainer.scrollTop / maxScrollY : 0
  }

  const oldWidth = markerInstance.targetWidth
  const oldHeight = markerInstance.targetHeight

  const availableWidth = containerWidth - PADDING * 2
  const availableHeight = containerHeight - PADDING * 2

  if (markerInstance.targetImage.src !== props.targetImage) {
    await updateMarkerImage(markerInstance, props.targetImage)
  }

  const imageAspectRatio = props.imageWidth / props.imageHeight

  let newWidth, newHeight

  switch (mode) {
    case 'fit-width':
      newWidth = availableWidth
      newHeight = newWidth / imageAspectRatio
      break

    case 'fit-height':
      newHeight = availableHeight
      newWidth = newHeight * imageAspectRatio
      break

    case 'fit-content':
    default:
      if (availableWidth / availableHeight > imageAspectRatio) {
        newHeight = availableHeight
        newWidth = newHeight * imageAspectRatio
      } else {
        newWidth = availableWidth
        newHeight = newWidth / imageAspectRatio
      }
      break
  }

  // @ts-expect-error replace loaded image with better resolution one
  const editingTarget = markerInstance._editingTarget as HTMLImageElement | undefined

  if (editingTarget) {
    editingTarget.src = props.targetImage
  }

  const scaleX = newWidth / oldWidth
  const scaleY = newHeight / oldHeight

  // @ts-expect-error fix scaling after image change
  markerInstance.scaleMarkers(scaleX, scaleY)
  markerInstance.targetWidth = newWidth
  markerInstance.targetHeight = newHeight

  if (scrollContainer) {
    setTimeout(() => {
      const newMaxScrollX = scrollContainer.scrollWidth - scrollContainer.clientWidth
      const newMaxScrollY = scrollContainer.scrollHeight - scrollContainer.clientHeight

      const newScrollX = newMaxScrollX * scrollXPercent
      const newScrollY = newMaxScrollY * scrollYPercent

      scrollContainer.scrollTo({
        left: newScrollX,
        top: newScrollY,
        behavior: 'auto',
      })
    }, 0)
  }
}

const applyShadowRootStyles = (shadowRoot: ShadowRoot) => {
  const style = document.createElement('style')

  style.textContent = `
    .canvas-container > svg {
      transform: none !important;
      position: relative;
    }
  `

  shadowRoot.appendChild(style)
}

// Detects when MarkerJs component is disconnected. Emits repaint to recreate Vue component.
// Happens when component is moved (e.g. reordered), but Vue keeps the component instance (the same key property),
// then MarkerJs disconnects and connects again, but with defects. Manual repaint fixes MarkerJs issue.
const repaintOnDisconnectedCallback = (markerArea: MarkerAreaType) => {
  // @ts-expect-error access private property
  const originalDetachEvents = markerArea.detachEvents

  function detachEvents() {
    originalDetachEvents.call(markerArea)
    emit('repaint')
  }

  // @ts-expect-error access private property
  markerArea.detachEvents = detachEvents.bind(markerArea)
}

onMounted(async () => {
  if (!editorContainer.value) {
    return
  }

  const {
    markerjs3: { MarkerArea },
    laserPointer: { LaserPointerMarker, LaserPointerMarkerEditor },
  } = await useMarkerjs3()

  imageElement.src = props.targetImage
  editor.value = new MarkerArea()

  // disable changing tool to selection on double click
  editor.value.addEventListener(
    'dblclick',
    (e) => {
      e.stopImmediatePropagation()
      e.preventDefault()
    },
    { capture: true },
  )

  editor.value.targetImage = imageElement
  editor.value.registerMarkerType(LaserPointerMarker, LaserPointerMarkerEditor)

  repaintOnDisconnectedCallback(editor.value)

  if (editor.value.shadowRoot) {
    applyShadowRootStyles(editor.value.shadowRoot)
  }

  scaleMarkerArea(editor.value, props.containerWidth, props.containerHeight, props.scalingMode)
  editorContainer.value.appendChild(editor.value)
})

watch(
  [isActive, editor],
  () => {
    if (isActive.value && editor.value) {
      zoomInstance.resetZoom()
      annotationLayer.setActiveMarker(editor.value)
    }
  },
  { immediate: true },
)

watch([targetImage, imageWidth, imageHeight, containerWidth, containerHeight, zoom, scalingMode], () => {
  if (editor.value) {
    scaleMarkerArea(editor.value, props.containerWidth, props.containerHeight, props.scalingMode)
  }
})
</script>
